<script setup lang="ts">
import { useInformationalEventsApi } from '~/composables/api/use-informational-events-api';
import { useMyInterests } from '~/stores/use-my-interests';

// Informational (third-party) events list. Rendered as the "More to discover"
// section on the combined `/browse` surface. The former standalone `/discover`
// list now 301-redirects to `/browse`, so this section is no longer mounted
// anywhere on its own. The `pageParam` prop names the query key this section
// uses for its pagination so it never collides with the community list's `page`
// on the shared `/browse` page. `kind` and `range` are section-specific and
// don't collide.
const props = withDefaults(
  defineProps<{
    pageParam?: string;
  }>(),
  {
    pageParam: 'dpage',
  },
);

const config = useRuntimeConfig();
const route = useRoute();

// `route.query.kind` is `string | string[] | undefined`; normalize to a stable
// string array so it can drive both the API params and the chip active-state.
const selectedKinds = computed<string[]>(() => {
  const raw = route.query.kind;
  if (Array.isArray(raw)) return raw.filter((k): k is string => k != null);
  if (typeof raw === 'string' && raw.length > 0) return [raw];
  return [];
});

const activePreset = computed<DiscoverDatePresetKey>(() =>
  normalizeDiscoverDatePreset(route.query.range as string | undefined),
);

const currentPage = computed(() => {
  const p = Number(route.query[props.pageParam]);
  return p > 0 ? p : 1;
});

const { data, status, refresh } = useAsyncData(
  `discover-informational-events-${props.pageParam}`,
  () => {
    const params: {
      'event_kind[]'?: string[];
      date_from?: string;
      date_to?: string;
      page?: number;
    } = {};

    if (selectedKinds.value.length > 0) {
      params['event_kind[]'] = selectedKinds.value;
    }

    const range = discoverDateRange(activePreset.value);
    if (range.date_from) params.date_from = range.date_from;
    if (range.date_to) params.date_to = range.date_to;

    if (currentPage.value > 1) params.page = currentPage.value;

    return useInformationalEventsApi().list(params);
  },
  {
    // Watch only this section's own reactive inputs so unrelated (community)
    // query changes on a shared page don't trigger a refetch here.
    watch: [selectedKinds, activePreset, currentPage],
    server: !config.public.mock,
  },
);

// Toggle a kind in/out of the selected set; always resets this section's
// pagination. Spread `...route.query` so other sections' params survive.
function toggleKind(kind: string) {
  const next = selectedKinds.value.includes(kind)
    ? selectedKinds.value.filter((k) => k !== kind)
    : [...selectedKinds.value, kind];

  navigateTo({
    query: {
      ...route.query,
      kind: next.length > 0 ? next : undefined,
      [props.pageParam]: undefined,
    },
  });
}

function clearKinds() {
  navigateTo({
    query: {
      ...route.query,
      kind: undefined,
      [props.pageParam]: undefined,
    },
  });
}

function setPreset(key: DiscoverDatePresetKey) {
  navigateTo({
    query: {
      ...route.query,
      range: key === 'anytime' ? undefined : key,
      [props.pageParam]: undefined,
    },
  });
}

function setPage(page: number) {
  navigateTo({
    query: {
      ...route.query,
      [props.pageParam]: page > 1 ? page : undefined,
    },
  });
}

// Warm the shared interest store so the informational cards' interest toggles
// seed their current rung once auth settles. Client + authed only; the store
// dedupes across the browse page's community + discover surfaces.
onMounted(() => {
  useMyInterests().ensureUpcoming();
});
</script>

<template>
  <div>
    <!-- Kind Filter -->
    <v-chip-group class="mb-2" multiple>
      <v-chip
        :color="selectedKinds.length === 0 ? 'primary' : undefined"
        :variant="selectedKinds.length === 0 ? 'flat' : 'outlined'"
        @click="clearKinds"
      >
        All
      </v-chip>
      <v-chip
        v-for="kind in INFORMATIONAL_EVENT_KINDS"
        :key="kind"
        :color="selectedKinds.includes(kind) ? 'primary' : undefined"
        :variant="selectedKinds.includes(kind) ? 'flat' : 'outlined'"
        :prepend-icon="informationalEventKindMeta(kind).icon"
        @click="toggleKind(kind)"
      >
        {{ informationalEventKindMeta(kind).label }}
      </v-chip>
    </v-chip-group>

    <!-- Date-Range Preset -->
    <v-select
      :model-value="activePreset"
      :items="DISCOVER_DATE_PRESETS"
      item-title="label"
      item-value="key"
      label="When"
      variant="outlined"
      density="comfortable"
      hide-details
      class="mb-6"
      style="max-width: 260px"
      @update:model-value="setPreset"
    />

    <!-- Loading State (idle = SSR placeholder when server fetch is skipped) -->
    <v-row v-if="status === 'pending' || status === 'idle'">
      <v-col v-for="n in 6" :key="n" cols="12" sm="6" md="4">
        <v-skeleton-loader type="card" />
      </v-col>
    </v-row>

    <!-- Error State -->
    <v-alert
      v-else-if="status === 'error'"
      type="error"
      variant="tonal"
      class="mb-6"
    >
      <div class="d-flex align-center justify-space-between">
        <span>Failed to load events. Please try again.</span>
        <v-btn variant="text" color="error" @click="refresh()"> Retry </v-btn>
      </div>
    </v-alert>

    <!-- Empty State -->
    <div v-else-if="!data?.data.length" class="text-center py-12">
      <v-icon size="64" color="medium-emphasis" class="mb-4">
        mdi-calendar-search-outline
      </v-icon>
      <p class="text-h6 text-medium-emphasis">
        No informational events match your filters
      </p>
      <p class="text-body-2 text-medium-emphasis">
        Try broadening your date range or clearing the kind filter.
      </p>
    </div>

    <!-- Event Grid -->
    <template v-else>
      <v-row>
        <v-col
          v-for="event in data.data"
          :key="event.id"
          cols="12"
          sm="6"
          md="4"
        >
          <DiscoverInformationalEventCard :event="event" />
        </v-col>
      </v-row>

      <!-- Pagination -->
      <div v-if="data.meta.last_page > 1" class="d-flex justify-center mt-8">
        <v-pagination
          :model-value="data.meta.current_page"
          :length="data.meta.last_page"
          @update:model-value="setPage"
        />
      </div>
    </template>
  </div>
</template>
